import { useState } from 'react'
import { Plus, Trash2, Edit2, Loader2 } from 'lucide-react'
import {
  useFaq, useCreateFaqCategory, useDeleteFaqCategory,
  useCreateFaqItem, useUpdateFaqItem, useDeleteFaqItem,
} from '@/hooks/useNaiDesk'

export default function FaqAdmin() {
  const { data, isLoading } = useFaq()
  const createCat = useCreateFaqCategory()
  const deleteCat = useDeleteFaqCategory()
  const createItem = useCreateFaqItem()
  const updateItem = useUpdateFaqItem()
  const deleteItem = useDeleteFaqItem()

  const [showCatForm, setShowCatForm] = useState(false)
  const [catName, setCatName] = useState('')
  const [catSlug, setCatSlug] = useState('')

  const [editingItem, setEditingItem] = useState<{ id?: string; category_id: string; question: string; answer: string; display_order: number; is_active: boolean } | null>(null)

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">FAQ — Admin</h1>
          <p className="text-sm text-gray-500">Categorias e itens da Base de Conhecimento</p>
        </div>
        <button onClick={() => setShowCatForm(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
          <Plus className="h-4 w-4" />Nova categoria
        </button>
      </header>

      {showCatForm && (
        <form onSubmit={async e => { e.preventDefault(); await createCat.mutateAsync({ name: catName, slug: catSlug, display_order: 0 }); setShowCatForm(false); setCatName(''); setCatSlug('') }}
          className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4 mb-4 flex gap-2">
          <input value={catName} onChange={e => setCatName(e.target.value)} placeholder="Nome" required className="flex-1 px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
          <input value={catSlug} onChange={e => setCatSlug(e.target.value)} placeholder="slug-url" required pattern="[a-z0-9-]+" className="flex-1 px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
          <button type="submit" className="px-4 py-2 rounded bg-emerald-600 text-white text-sm">Criar</button>
          <button type="button" onClick={() => setShowCatForm(false)} className="px-3 py-2 text-sm">Cancelar</button>
        </form>
      )}

      {isLoading && <Loader2 className="h-5 w-5 animate-spin" />}

      <div className="space-y-6">
        {data?.map(cat => (
          <section key={cat.id} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <header className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
              <div>
                <h2 className="font-semibold">{cat.name}</h2>
                <span className="text-xs text-gray-500">/{cat.slug}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditingItem({ category_id: cat.id, question: '', answer: '', display_order: 0, is_active: true })}
                  className="text-xs px-3 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 inline-flex items-center gap-1"><Plus className="h-3 w-3" />Item</button>
                <button onClick={() => confirm('Apagar categoria e itens?') && deleteCat.mutate(cat.id)}
                  className="text-xs px-3 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /></button>
              </div>
            </header>
            <ul>
              {cat.items.map(it => (
                <li key={it.id} className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{it.question}</p>
                    <p className="text-xs text-gray-500">{it.is_active ? 'ativo' : 'inativo'} · ordem {it.display_order}</p>
                  </div>
                  <button onClick={() => setEditingItem({ id: it.id, category_id: cat.id, question: it.question, answer: it.answer, display_order: it.display_order, is_active: it.is_active })}
                    className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1"><Edit2 className="h-3 w-3" />Editar</button>
                  <button onClick={() => confirm('Apagar item?') && deleteItem.mutate(it.id)} className="text-red-600 hover:underline text-xs"><Trash2 className="h-3 w-3" /></button>
                </li>
              ))}
              {cat.items.length === 0 && <li className="px-4 py-3 text-xs text-gray-500">Sem itens</li>}
            </ul>
          </section>
        ))}
      </div>

      {editingItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingItem(null)}>
          <form onClick={e => e.stopPropagation()} onSubmit={async e => {
            e.preventDefault()
            if (editingItem.id) await updateItem.mutateAsync({ id: editingItem.id, payload: editingItem })
            else await createItem.mutateAsync(editingItem)
            setEditingItem(null)
          }} className="bg-white dark:bg-gray-900 rounded-xl max-w-2xl w-full p-6 space-y-3">
            <h3 className="font-semibold text-lg">{editingItem.id ? 'Editar item' : 'Novo item'}</h3>
            <input value={editingItem.question} onChange={e => setEditingItem({ ...editingItem, question: e.target.value })}
              placeholder="Pergunta" required className="w-full px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
            <textarea value={editingItem.answer} onChange={e => setEditingItem({ ...editingItem, answer: e.target.value })}
              placeholder="Resposta (HTML permitido)" rows={6} required className="w-full px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-mono" />
            <div className="flex gap-3">
              <input type="number" value={editingItem.display_order} onChange={e => setEditingItem({ ...editingItem, display_order: Number(e.target.value) })}
                placeholder="Ordem" className="w-32 px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editingItem.is_active} onChange={e => setEditingItem({ ...editingItem, is_active: e.target.checked })} />Ativo</label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditingItem(null)} className="px-3 py-1.5 text-sm">Cancelar</button>
              <button type="submit" className="px-4 py-1.5 rounded bg-blue-600 text-white text-sm font-semibold">Salvar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
